import {Col, Row, Skeleton, Tooltip} from 'antd';
import dayjs from 'dayjs';
import {useContext, useEffect, useMemo} from 'react';

import {EmptyCell} from '../../../../../../layout/table/empty-cell/empty-cell';
import {FormattedNumber} from '../../../../../../provider/locale/formatted-number';
import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {MainFilterContext} from '../../../../../../provider/main-filter/main-filter';
import {MainFilterContextType} from '../../../../../../provider/main-filter/main-filter-type';
import {SystemContext} from '../../../../../../provider/system/system-context';
import {SystemContextType} from '../../../../../../provider/system/system-context-type';
import {classNames} from '../../../../../../util/css';
import {useSummaryStats} from '../competitors-page-hook';

import {DeltaNumber} from './delta-number/delta-number';
import {statisticsTemplate} from './statistics-cards-helper';
import {StatisticsCardTemplateItemType, StatisticsCardType} from './statistics-cards-type';
import * as styles from './statistic-cards.scss';

export function StatisticsCard(): JSX.Element {
    const systemContext = useContext<SystemContextType>(SystemContext);
    const {screen} = systemContext;
    const {isMobile} = screen;

    const {mainFilterKey} = useContext<MainFilterContextType>(MainFilterContext);

    const {getLocalizedString} = useLocale();

    const {mutateAsync: fetchSummary, data, isLoading: isDataInProgress, isError} = useSummaryStats();

    function getTooltipTitle(deltaValue: number | null) {
        if (!deltaValue) {
            return '';
        }

        if (deltaValue > 0) {
            return getLocalizedString('REVIEWS_ANALYSIS__COMPETITORS__TOOLTIP_TITLE', {
                count: deltaValue.toString(),
            });
        }

        return getLocalizedString('REVIEWS_ANALYSIS__COMPETITORS__TOOLTIP_TITLE_NEGATIVE', {
            count: deltaValue.toString().slice(1),
        });
    }

    useEffect(() => {
        fetchSummary({
            // eslint-disable-next-line no-undefined
            filterKey: mainFilterKey || undefined,
            currentDate: dayjs().format('YYYY-MM-DD'),
            comparedDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        });
    }, [fetchSummary, mainFilterKey]);

    const statisticsCards: Array<StatisticsCardType> = useMemo<Array<StatisticsCardType>>(
        (): Array<StatisticsCardType> =>
            statisticsTemplate.map((item: StatisticsCardTemplateItemType): StatisticsCardType => {
                const {value, delta} = item.getValueAndDelta(data);

                return {
                    langKey: item.langKey,
                    value:
                        value !== null ? <FormattedNumber options={item.formatOptions} value={value} /> : <EmptyCell />,
                    delta: delta !== null ? <DeltaNumber delta={delta} invertColor={item.isInvertColor} /> : <div />,
                    deltaValue: delta,
                };
            }),
        [data]
    );

    return (
        <div className={styles.MainStatisticsSingle}>
            {!isError && (
                <Row className={styles.MainStatisticsSingle_row} gutter={[24, 24]} wrap>
                    {statisticsCards.map((card: StatisticsCardType): JSX.Element => {
                        return (
                            <Tooltip
                                getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                                key={card.langKey}
                                title={getTooltipTitle(card.deltaValue)}
                                trigger="hover"
                            >
                                <Col
                                    className={classNames(styles.MainStatisticsSingle_column, {
                                        [styles.MainStatisticsSingle_column_columnMobile]: isMobile,
                                    })}
                                    key={card.langKey}
                                    span={isMobile ? 24 : 8}
                                >
                                    <div className={styles.MainStatisticsSingle_card}>
                                        <label className={styles.MainStatisticsSingle_card__label}>
                                            <Locale stringKey={card.langKey} />

                                            <output className={styles.MainStatisticsSingle_card__value}>
                                                {isDataInProgress ? (
                                                    <Skeleton.Button active />
                                                ) : (
                                                    <>
                                                        {card.value} {card.delta}
                                                    </>
                                                )}
                                            </output>
                                        </label>
                                    </div>
                                </Col>
                            </Tooltip>
                        );
                    })}
                </Row>
            )}
        </div>
    );
}
